import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import DealAPI from 'dashboard/api/deals';
import { createStore } from 'dashboard/store/storeFactory';
import { throwErrorMessage } from 'dashboard/store/utils/api';

const camelizeDeal = data => camelcaseKeys(data || [], { deep: true });

export const useDealsStore = createStore({
  name: 'deals',
  type: 'pinia',
  API: DealAPI,
  state: () => ({
    dealsByStage: {},
    summary: { byStage: {}, totals: { open: {}, wonMonth: {}, lostMonth: {} } },
    report: {
      open: {},
      won: {},
      lost: {},
      wonCount: 0,
      lostCount: 0,
      wonByMonth: [],
      byAssignee: [],
    },
  }),

  getters: {
    dealsForStage: state => stageId => state.dealsByStage[stageId] || [],
    stageTotals: state => stageId =>
      state.summary.byStage[stageId] ||
      state.summary.byStage[String(stageId)] ||
      {},
  },

  actions: () => ({
    // `DealsController#create` renders `show`, whose jbuilder view puts the
    // deal fields at the JSON root (no `payload` wrapper) — unlike `index`,
    // which wraps the list in `payload`. Destructure `data` directly here.
    async create(payload) {
      this.setUIFlag({ creatingItem: true });
      try {
        const { data } = await DealAPI.create(snakecaseKeys(payload));
        const deal = camelizeDeal(data);

        const bucket = [...(this.dealsByStage[deal.dealStageId] || [])];
        bucket.push(deal);
        this.dealsByStage = {
          ...this.dealsByStage,
          [deal.dealStageId]: bucket,
        };

        return deal;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ creatingItem: false });
      }
    },

    // `DealsController#update` renders `show` (no `payload` envelope, same
    // contract as `create`/`moveToStage`) — read `data` directly. Editing
    // title/value/notes never changes the stage, so the deal is re-inserted
    // into the SAME bucket it already lives in.
    async updateDeal(dealId, payload) {
      this.setUIFlag({ updatingItem: true });
      try {
        const { data } = await DealAPI.update(dealId, snakecaseKeys(payload));
        const deal = camelizeDeal(data);

        this.removeDealFromAllStages(deal.id);
        const bucket = [...(this.dealsByStage[deal.dealStageId] || [])];
        bucket.push(deal);
        this.dealsByStage = {
          ...this.dealsByStage,
          [deal.dealStageId]: bucket,
        };

        return deal;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ updatingItem: false });
      }
    },

    async deleteDeal(dealId) {
      this.setUIFlag({ deletingItem: true });
      try {
        await DealAPI.delete(dealId);
        this.removeDealFromAllStages(dealId);

        // Deleting a deal changes the totals (it drops out of `open` or
        // whichever stage it lived in), so refresh the summary — same
        // fire-and-forget pattern as the success path of `moveToStage`.
        this.fetchSummary();

        return null;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ deletingItem: false });
      }
    },

    removeDealFromAllStages(dealId) {
      Object.keys(this.dealsByStage).forEach(stageId => {
        this.dealsByStage[stageId] = this.dealsByStage[stageId].filter(
          deal => deal.id !== dealId
        );
      });
    },

    // The summary endpoint's inner keys are currency codes (`BRL`/`USD`) and
    // stage ids, not JS-style field names — a deep camelcaseKeys would
    // mangle `BRL` into `bRL`. Only the top-level envelope is camelised
    // here; everything under `by_stage`/`totals.*` is read via its
    // snake_case key and re-keyed by hand.
    async fetchSummary() {
      try {
        const { data } = await DealAPI.getSummary();
        this.summary = {
          byStage: data.by_stage || {},
          totals: {
            open: data.totals?.open || {},
            wonMonth: data.totals?.won_month || {},
            lostMonth: data.totals?.lost_month || {},
          },
        };
      } catch {
        // Don't let a summary failure break the board — just skip the
        // update and keep showing the last known totals.
      }
    },

    // Same reasoning as `fetchSummary`: the currency maps (`BRL`/`USD`) and
    // `month` inside `won_by_month`/`by_assignee` are not JS-style field
    // names, so only the envelope is camelised here — a deep camelcaseKeys
    // would mangle `BRL` into `bRL`.
    async fetchReport({ from, to }) {
      try {
        const { data } = await DealAPI.getReport({ from, to });
        this.report = {
          open: data.open || {},
          won: data.won || {},
          lost: data.lost || {},
          wonCount: data.won_count || 0,
          lostCount: data.lost_count || 0,
          wonByMonth: data.won_by_month || [],
          byAssignee: data.by_assignee || [],
        };
      } catch {
        // Don't let a report failure break the page — just skip the update
        // and keep showing the last known report.
      }
    },

    async fetchByStage(stageId, page = 1, filters = {}) {
      this.setUIFlag({ fetchingList: true });
      try {
        const {
          data: { payload },
        } = await DealAPI.get({ page, dealStageId: stageId, ...filters });
        this.dealsByStage = {
          ...this.dealsByStage,
          [stageId]: camelizeDeal(payload),
        };
        return this.dealsByStage[stageId];
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async moveToStage(dealId, dealStageId, position) {
      this.setUIFlag({ updatingItem: true });

      // `CrmColumn.vue` binds vuedraggable's `:list` directly to this
      // store's `dealsForStage(stageId)` array (by reference), so a drag
      // already spliced the card into `dealsByStage[dealStageId]` — and out
      // of its origin bucket — before this action even runs. The dragged
      // deal object itself is untouched, so its stale `dealStageId` field
      // still names the origin stage. Reading it here (instead of taking a
      // `fromStageId` param) lets the rollback below know which bucket to
      // restore without any caller change.
      const draggedDeal = (this.dealsByStage[dealStageId] || []).find(
        deal => deal.id === dealId
      );
      const fromStageId = draggedDeal?.dealStageId;

      try {
        // `DealsController#update` renders `show`, whose jbuilder view puts
        // the deal fields at the JSON root (no `payload` wrapper) — same
        // contract as `create`. Only `index` wraps in `payload`.
        const { data } = await DealAPI.update(dealId, {
          deal_stage_id: dealStageId,
          position,
        });
        const deal = camelizeDeal(data);

        this.removeDealFromAllStages(deal.id);
        const bucket = [...(this.dealsByStage[dealStageId] || [])];
        const insertAt = Math.min(position ?? bucket.length, bucket.length);
        bucket.splice(insertAt, 0, deal);
        this.dealsByStage = { ...this.dealsByStage, [dealStageId]: bucket };

        // Moving a card changes the totals (including won/lost-this-month),
        // so refresh the summary. Fire-and-forget: a summary hiccup
        // shouldn't fail the move itself.
        this.fetchSummary();

        return deal;
      } catch (error) {
        // The PATCH failed but the card already left its column in the UI
        // (see comment above) — re-fetch the real state of both stages from
        // the server so the card snaps back to where it actually lives,
        // instead of staying stuck in the wrong column until reload.
        const staleStageIds = [fromStageId, dealStageId].filter(
          (id, index, ids) => id != null && ids.indexOf(id) === index
        );
        await Promise.all(staleStageIds.map(id => this.fetchByStage(id)));

        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ updatingItem: false });
      }
    },
  }),
});
